import type {
  BundleRetirementCommand,
  BundleRetirementPreview,
} from "@reminder-tracker/backend-contracts";
import { useConvex, useMutation, useQuery } from "convex/react";
import { useState } from "react";
import {
  listBundleAdministrationTasks,
  previewBundleRetirement,
  retireBundleCatalogue,
} from "../../backend/functions";

function key(prefix: "corr" | "cmd") {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}

export function BundleRetirementTasks({ environment }: Readonly<{ environment: string }>) {
  const [correlationId] = useState(() => key("corr"));
  const page = useQuery(listBundleAdministrationTasks, { area: "retirement", correlationId });
  const client = useConvex();
  const retire = useMutation(retireBundleCatalogue);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<BundleRetirementPreview | null>(null);
  const [approval, setApproval] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<BundleRetirementCommand | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const identity = (bundleId: string, version: number) => `${bundleId}/${version}`;

  const review = async () => {
    if (page === undefined || selected.size === 0 || selected.size > 25 || busy) return;
    setBusy(true);
    setConfirmed(false);
    setPreview(null);
    setFeedback(null);
    try {
      const entries = page.items
        .filter((item) => selected.has(identity(item.bundleId, item.version)))
        .map((item) => ({
          bundleId: item.bundleId,
          bundleVersion: item.version,
          expectedRevision: item.revision,
          contentDigest: item.contentDigest,
        }));
      setPreview(await client.query(previewBundleRetirement, { entries, correlationId }));
      setPending(null);
    } catch {
      setFeedback(
        "The retirement preview could not be loaded. Review the current versions and try again.",
      );
    } finally {
      setBusy(false);
    }
  };
  const submit = async (request: BundleRetirementCommand) => {
    if (busy) return;
    setPending(request);
    setBusy(true);
    setFeedback(null);
    try {
      const result = await retire(request);
      setFeedback(
        `${result.retiredVersionCount} bundle versions retired from discovery. Saved reminders and entered drafts are unchanged.`,
      );
      setPending(null);
      setPreview(null);
      setSelected(new Set());
      setConfirmed(false);
    } catch {
      setFeedback(
        "Retirement is not confirmed. Retry the same request to check its recorded result, or review the current inventory again if the preview changed.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (environment === "future-live")
    return <p>Editorial retirement is not approved for Future Live.</p>;
  return (
    <section aria-labelledby="bundle-retirement-heading" className="bundle-retirement">
      <h3 id="bundle-retirement-heading">Retire old bundles from discovery</h3>
      <p>
        Use this for catalogue replacement. Existing reminders, history and entered drafts stay
        unchanged. Owners review retired drafts before creating more reminders.
      </p>
      {page === undefined ? (
        <p role="status">Loading published bundle versions…</p>
      ) : (
        <>
          {page.truncated ? (
            <p role="alert">
              This is a partial inventory. Review exact versions in batches; do not treat this page
              as the complete catalogue.
            </p>
          ) : null}
          {page.items.length === 0 ? (
            <p>No published bundle versions are available to retire.</p>
          ) : (
            <>
              <p>Select up to 25 exact versions for one retirement review.</p>
              <button
                type="button"
                disabled={busy || page.items.length > 25 || page.truncated}
                onClick={() => {
                  setSelected(
                    new Set(page.items.map((item) => identity(item.bundleId, item.version))),
                  );
                  setPreview(null);
                  setConfirmed(false);
                }}
              >
                Select all {page.items.length} listed versions
              </button>
              <ul className="bundle-release-list">
                {page.items.map((item) => (
                  <li key={identity(item.bundleId, item.version)}>
                    <label>
                      <input
                        type="checkbox"
                        disabled={busy}
                        checked={selected.has(identity(item.bundleId, item.version))}
                        onChange={() => {
                          const next = new Set(selected);
                          const id = identity(item.bundleId, item.version);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          setSelected(next);
                          setPreview(null);
                          setConfirmed(false);
                        }}
                      />
                      {item.title} · {item.market} · version {item.version}
                    </label>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={busy || selected.size === 0 || selected.size > 25}
                onClick={() => void review()}
              >
                Preview retirement for {selected.size} versions
              </button>
            </>
          )}
        </>
      )}
      {preview !== null ? (
        <div className="bundle-impact">
          <h4>Reviewed retirement inventory</h4>
          <p>Reason: catalogue replacement. This is separate from a safety withdrawal.</p>
          <ul>
            {preview.items.map((item) => (
              <li key={identity(item.bundleId, item.bundleVersion)}>
                {item.market}: {item.activeDraftCountCapped ? "More than " : ""}
                {item.activeDraftCount} active {item.activeDraftCount === 1 ? "draft" : "drafts"};{" "}
                {item.existingCreationGroupCountCapped ? "more than " : ""}
                {item.existingCreationGroupCount} saved creation{" "}
                {item.existingCreationGroupCount === 1 ? "batch" : "batches"}.
                <details>
                  <summary>
                    {item.bundleId} · version {item.bundleVersion}
                  </summary>
                  <p>Expected revision: {item.expectedRevision}</p>
                  <p>
                    Content digest: <code>{item.contentDigest}</code>
                  </p>
                </details>
              </li>
            ))}
          </ul>
          <label>
            SCRUM approval reference
            <input
              disabled={busy}
              value={approval}
              onChange={(event) => {
                setApproval(event.target.value);
                setConfirmed(false);
              }}
            />
          </label>
          <label>
            <input
              type="checkbox"
              disabled={busy}
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            I reviewed these exact versions and the preservation of existing reminders and drafts in{" "}
            {environment}.
          </label>
          <button
            type="button"
            disabled={
              busy ||
              !confirmed ||
              !/^SCRUM-[1-9][0-9]{0,7}:[A-Za-z0-9][A-Za-z0-9._-]{2,120}$/u.test(approval)
            }
            onClick={() =>
              void submit({
                entries: preview.items.map(
                  ({ bundleId, bundleVersion, expectedRevision, contentDigest }) => ({
                    bundleId,
                    bundleVersion,
                    expectedRevision,
                    contentDigest,
                  }),
                ),
                previewFingerprint: preview.previewFingerprint,
                approvalReference: approval,
                confirmed: true,
                commandKey: key("cmd"),
                correlationId,
              })
            }
          >
            Retire the reviewed catalogue
          </button>
        </div>
      ) : null}
      {busy ? <p role="status">Checking the exact retirement request…</p> : null}
      {feedback ? <p role="status">{feedback}</p> : null}
      {pending !== null && !busy ? (
        <button type="button" onClick={() => void submit(pending)}>
          Retry the same retirement request
        </button>
      ) : null}
    </section>
  );
}
